import {
  Alert,
  BottomSheet,
  Button,
  Card,
  Field,
  FieldHint,
  Input,
  Label,
  Picker,
  Textarea,
  cn,
} from "@breenova/ui";
import { Boxes, Home, LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { DetailHeader } from "@/components/app-shell";
import {
  buildLocationTree,
  countBirdsBelow,
  countCagesBelow,
  useCreateLocation,
  useDeleteLocation,
  useLocationTree,
  useUpdateLocation,
  type LocationKind,
  type LocationNode,
  type LocationTreeNode,
} from "@/lib/locations";
import { SettingsFrame } from "./settings-frame";

/**
 * Where a breeder builds and maintains the places birds live.
 *
 * There was no such screen. Cages could only ever arrive through the seed data or a
 * direct API call, so `Cage` on the add-bird form was a picker onto whatever happened to
 * exist — and for a new breeder, onto nothing at all. The write endpoints existed; only
 * the screen was missing.
 *
 * Drawn as the tree rather than as a flat list, because the tree is the thing being
 * managed. A breeder does not think "cage B-12"; they think "top row of the second
 * flight", and the three levels — aviary, section, cage — are that thought written down.
 * A flat list would also make the one rule that governs this screen invisible: only a
 * cage holds birds, so an aviary and a section are addresses, not places.
 */
export function LocationsPage() {
  const locations = useLocationTree(true);

  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const nodes = useMemo(() => locations.data ?? [], [locations.data]);
  const tree = useMemo(() => buildLocationTree(nodes), [nodes]);

  const cages = nodes.filter((node) => node.kind === "Cage");
  const birds = nodes.reduce((total, node) => total + node.birdCount, 0);

  return (
    <div className="flex flex-col gap-md md:gap-lg">
      <DetailHeader
        backTo="/more"
        kicker="Rooms and cages"
        title="Your aviary"
        action={
          <Button
            size="sm"
            onClick={() => {
              setError(null);
              setSheet({ mode: "create", kind: "Aviary", parentId: null });
            }}
          >
            <Plus className="size-4" aria-hidden />
            Add
          </Button>
        }
      />

      <SettingsFrame>
        <section className="flex flex-col gap-md" aria-live="polite">
          {error ? <Alert tone="error">{error}</Alert> : null}

          {locations.isPending ? (
            <p className="py-xl text-center text-body text-on-surface-variant">
              Loading your aviary…
            </p>
          ) : locations.isError ? (
            <Alert tone="error">Could not load your aviary.</Alert>
          ) : tree.length === 0 ? (
            <Card className="flex flex-col items-center gap-sm p-xl text-center">
              <p className="text-card-title text-on-surface">Nothing here yet</p>
              <p className="max-w-prose text-body text-on-surface-variant">
                Start with an aviary — the building or the room. Rows and flights go inside
                it, and cages go inside those. Only a cage holds birds.
              </p>
              <Button
                className="mt-sm"
                onClick={() => setSheet({ mode: "create", kind: "Aviary", parentId: null })}
              >
                <Plus className="size-4" aria-hidden />
                Add an aviary
              </Button>
            </Card>
          ) : (
            <>
              <p className="text-caption text-on-surface-variant" data-numeric>
                {cages.length} {cages.length === 1 ? "cage" : "cages"} · {birds}{" "}
                {birds === 1 ? "bird" : "birds"} placed
              </p>

              {/*
               * One column on a phone, two from `lg`. Each root aviary is a self-contained
               * card, so they tile without any of them being cut in half — which is why
               * this is a masonry-ish column split rather than a grid of equal rows.
               */}
              <ul className="grid grid-cols-1 gap-md lg:grid-cols-2 lg:items-start">
                {tree.map((root) => (
                  <li key={root.id}>
                    <LocationBranch
                      node={root}
                      onEdit={(target) => {
                        setError(null);
                        setSheet({ mode: "edit", node: target });
                      }}
                      onAddChild={(parent) => {
                        setError(null);
                        setSheet({
                          mode: "create",
                          kind: parent.kind === "Aviary" ? "Section" : "Cage",
                          parentId: parent.id,
                        });
                      }}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          <Card tone="recessed" flat className="flex flex-col gap-sm p-lg">
            <h2 className="text-label-caps uppercase text-on-surface-variant">How this nests</h2>
            <p className="text-detail text-on-surface-variant">
              An aviary is the building or the room. A section is a row, a flight or a wall.
              A cage is the place a bird actually is — and it is the only level a bird can be
              put in, which is why the Cage picker on a bird&rsquo;s form lists cages and
              nothing else.
            </p>
            <p className="text-detail text-on-surface-variant">
              A cage that has been dismantled should be marked inactive rather than removed.
              It disappears from the pickers, and every movement that ever pointed at it
              still reads correctly.
            </p>
          </Card>
        </section>
      </SettingsFrame>

      {sheet ? (
        <LocationSheet
          state={sheet}
          nodes={nodes}
          onClose={() => setSheet(null)}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

/** One aviary and everything under it, as a card. */
function LocationBranch({
  node,
  onEdit,
  onAddChild,
}: {
  node: LocationTreeNode;
  onEdit: (node: LocationNode) => void;
  onAddChild: (node: LocationNode) => void;
}) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <LocationRow node={node} depth={0} onEdit={onEdit} onAddChild={onAddChild} />
      <Descendants nodes={node.children} depth={1} onEdit={onEdit} onAddChild={onAddChild} />
    </Card>
  );
}

function Descendants({
  nodes,
  depth,
  onEdit,
  onAddChild,
}: {
  nodes: readonly LocationTreeNode[];
  depth: number;
  onEdit: (node: LocationNode) => void;
  onAddChild: (node: LocationNode) => void;
}) {
  return (
    <>
      {nodes.map((child) => (
        <div key={child.id}>
          <LocationRow node={child} depth={depth} onEdit={onEdit} onAddChild={onAddChild} />
          <Descendants
            nodes={child.children}
            depth={depth + 1}
            onEdit={onEdit}
            onAddChild={onAddChild}
          />
        </div>
      ))}
    </>
  );
}

const KIND_ICON = {
  Aviary: Home,
  Section: Boxes,
  Cage: LayoutGrid,
} as const;

/**
 * One location.
 *
 * Indented by depth rather than nested in bordered boxes: three levels of box inside a
 * 24px-radius card is four concentric rounded rectangles, and the structure stops being
 * readable at exactly the point it starts mattering. The indent is a padding token
 * multiplied, which is the one arithmetic the spacing scale is meant for.
 */
function LocationRow({
  node,
  depth,
  onEdit,
  onAddChild,
}: {
  node: LocationTreeNode;
  depth: number;
  onEdit: (node: LocationNode) => void;
  onAddChild: (node: LocationNode) => void;
}) {
  const Icon = KIND_ICON[node.kind];
  const birds = countBirdsBelow(node);
  const cages = countCagesBelow(node);

  const facts =
    node.kind === "Cage"
      ? [
          `${node.birdCount} ${node.birdCount === 1 ? "bird" : "birds"}`,
          node.capacity ? `holds ${node.capacity}` : null,
        ]
      : [
          `${cages} ${cages === 1 ? "cage" : "cages"}`,
          `${birds} ${birds === 1 ? "bird" : "birds"}`,
        ];

  const overfull = node.kind === "Cage" && node.capacity !== null && node.birdCount > node.capacity;

  return (
    <div
      className={cn(
        "flex min-h-16 items-center gap-md py-tight pr-md",
        "border-b border-outline-variant last:border-b-0",
        !node.isActive && "opacity-55",
      )}
      // The one arithmetic on the spacing scale: each level steps in by one `lg`.
      style={{ paddingLeft: `calc(var(--spacing-lg) + ${depth} * var(--spacing-lg))` }}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          node.kind === "Cage"
            ? "bg-primary-fixed text-on-primary-fixed-variant"
            : "bg-surface-container text-on-surface-variant",
        )}
      >
        <Icon className="size-5" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-hair">
        <span className="flex items-center gap-sm">
          <span className="truncate text-label text-on-surface" data-numeric>
            {node.code}
          </span>
          {!node.isActive ? (
            <span className="shrink-0 text-tile-label uppercase text-on-surface-variant">
              Inactive
            </span>
          ) : null}
        </span>

        <span
          className={cn(
            "truncate text-caption",
            overfull ? "text-error" : "text-on-surface-variant",
          )}
          data-numeric
        >
          {[node.name, ...facts].filter(Boolean).join(" · ")}
          {overfull ? " · over capacity" : ""}
        </span>
      </div>

      {/* Only a cage is a leaf. Offering "add inside" on one would offer a nesting the
          domain refuses, and a button whose only outcome is an error is worse than none. */}
      {node.kind !== "Cage" ? (
        <button
          type="button"
          onClick={() => onAddChild(node)}
          aria-label={`Add inside ${node.code}`}
          className={cn(
            "flex size-touch shrink-0 items-center justify-center rounded-full",
            "text-on-surface-variant transition-colors hover:bg-surface-container",
          )}
        >
          <Plus className="size-4.5" aria-hidden />
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => onEdit(node)}
        aria-label={`Edit ${node.code}`}
        className={cn(
          "flex size-touch shrink-0 items-center justify-center rounded-full",
          "text-on-surface-variant transition-colors hover:bg-surface-container",
        )}
      >
        <Pencil className="size-4.5" aria-hidden />
      </button>
    </div>
  );
}

type SheetState =
  | { mode: "create"; kind: LocationKind; parentId: string | null }
  | { mode: "edit"; node: LocationNode };

const KIND_OPTIONS: readonly { value: LocationKind; label: string; hint: string }[] = [
  { value: "Aviary", label: "Aviary", hint: "A building or a room. Always at the top." },
  { value: "Section", label: "Section", hint: "A row, a flight or a wall inside an aviary." },
  { value: "Cage", label: "Cage", hint: "The only level a bird can be placed in." },
];

/**
 * Add or edit, in one sheet.
 *
 * The kind is fixed once a location exists. Turning a cage holding six birds into a
 * section is not an edit — it is a different location, and those six birds would be
 * standing in something that is no longer allowed to hold them. The server refuses it
 * too; the field is simply not offered, so the refusal never has to be explained.
 */
function LocationSheet({
  state,
  nodes,
  onClose,
  onError,
}: {
  state: SheetState;
  nodes: readonly LocationNode[];
  onClose: () => void;
  onError: (message: string | null) => void;
}) {
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const remove = useDeleteLocation();

  const editing = state.mode === "edit" ? state.node : undefined;

  const [kind, setKind] = useState<LocationKind>(
    state.mode === "create" ? state.kind : state.node.kind,
  );
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [capacity, setCapacity] = useState(editing?.capacity?.toString() ?? "");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [parentId, setParentId] = useState(
    state.mode === "create" ? (state.parentId ?? "") : (state.node.parentId ?? ""),
  );
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  /*
   * What may hold this kind, which is the same rule the domain enforces on save: a
   * section goes in an aviary, a cage in either, an aviary nowhere. Offering the illegal
   * parents and letting the server reject them would be a slower way to say the same
   * thing — and this way the picker is short enough to read.
   *
   * A node cannot be its own parent, and its own descendants are excluded too: putting a
   * row inside one of its own cages detaches the whole branch.
   */
  const parents = useMemo(() => {
    if (kind === "Aviary") {
      return [];
    }

    const banned = editing ? descendantsOf(editing.id, nodes) : new Set<string>();

    return nodes.filter(
      (node) =>
        node.id !== editing?.id &&
        !banned.has(node.id) &&
        (kind === "Section" ? node.kind === "Aviary" : node.kind !== "Cage"),
    );
  }, [kind, nodes, editing]);

  const busy = create.isPending || update.isPending || remove.isPending;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    const trimmed = code.trim();

    if (!trimmed) {
      setMessage("A location needs a code — whatever is painted on the door.");
      return;
    }

    // An aviary is always a root. Sending a stale parent from a kind switch would be
    // rejected, correctly, with a message about nesting that the breeder never asked for.
    const parent = kind === "Aviary" ? null : parentId || null;

    if (kind !== "Aviary" && !parent) {
      setMessage(`A ${kind.toLowerCase()} has to sit inside something.`);
      return;
    }

    const parsed = capacity.trim() ? Number(capacity.trim()) : null;

    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 1)) {
      setMessage("Capacity is a whole number of birds, or blank.");
      return;
    }

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            // The route supplies this too and the handler overwrites the body's copy
            // with it. It is in the command record, so it is in the generated body type,
            // and sending it is cheaper than casting the type away.
            id: editing.id,
            code: trimmed,
            name: name.trim() || null,
            parentId: parent,
            capacity: parsed,
            notes: notes.trim() || null,
            isActive,
          },
        });
      } else {
        await create.mutateAsync({
          code: trimmed,
          name: name.trim() || null,
          kind,
          parentId: parent,
          capacity: parsed,
          notes: notes.trim() || null,
        });
      }

      onError(null);
      onClose();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "That could not be saved.");
    }
  }

  async function confirmRemove(): Promise<void> {
    if (!editing) {
      return;
    }

    try {
      await remove.mutateAsync(editing.id);
      onError(null);
      onClose();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "That could not be removed.");
      setConfirmingRemove(false);
    }
  }

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.code}` : `Add ${kind.toLowerCase()}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          <Button
            type="submit"
            form="location-form"
            className="flex-1"
            disabled={busy}
          >
            {busy ? "Saving…" : editing ? "Save" : "Add"}
          </Button>
        </>
      }
    >
      <form id="location-form" onSubmit={(event) => void submit(event)} className="flex flex-col gap-lg">
        {message ? <Alert tone="error">{message}</Alert> : null}

        {editing ? null : (
          <Field>
            <Label>Level</Label>
            <Picker
              label="Level"
              value={kind}
              options={KIND_OPTIONS}
              onChange={(next) => {
                setKind(next);

                // A parent chosen for a section is rarely legal for a cage and never
                // legal for an aviary. Clearing is honest; carrying it over is a
                // rejection waiting to happen.
                setParentId("");
              }}
            />
          </Field>
        )}

        {kind === "Aviary" ? null : (
          <Field>
            <Label>Inside</Label>
            <Picker
              label="Inside"
              placeholder="Choose where this sits…"
              emptyLabel={
                kind === "Section" ? "Add an aviary first" : "Add an aviary or section first"
              }
              value={parentId}
              onChange={setParentId}
              options={parents.map((parent) => ({
                value: parent.id,
                label: parent.path,
                ...(parent.isActive ? {} : { hint: "Inactive" }),
              }))}
            />
          </Field>
        )}

        <Field>
          <Label htmlFor="location-code">Code</Label>
          <Input
            id="location-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={kind === "Cage" ? "B-12" : "Main aviary"}
            autoComplete="off"
            maxLength={32}
          />
          <FieldHint>
            What is painted on the door. It has to be unique across your whole aviary,
            because it is how a cage is identified everywhere else.
          </FieldHint>
        </Field>

        <Field>
          <Label htmlFor="location-name">Name</Label>
          <Input
            id="location-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Optional — when the code alone is not obvious"
            autoComplete="off"
            maxLength={200}
          />
        </Field>

        {kind === "Cage" ? (
          <Field>
            <Label htmlFor="location-capacity">Capacity</Label>
            <Input
              id="location-capacity"
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
              inputMode="numeric"
              placeholder="Optional"
              autoComplete="off"
            />
            <FieldHint>
              Advisory only. Nothing stops you putting more birds in during a move; the
              cage just says so.
            </FieldHint>
          </Field>
        ) : null}

        <Field>
          <Label htmlFor="location-notes">Notes</Label>
          <Textarea
            id="location-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            maxLength={2000}
          />
        </Field>

        {editing ? (
          <>
            <label
              className={cn(
                "flex min-h-touch items-center justify-between gap-md rounded-inset px-lg py-md",
                "bg-surface-container-low",
              )}
            >
              <span className="flex flex-col gap-hair">
                <span className="text-label text-on-surface">In use</span>
                <span className="text-caption text-on-surface-variant">
                  Turn off for a cage that has been dismantled. Its history stays.
                </span>
              </span>

              <input
                type="checkbox"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                className="size-5 shrink-0 accent-[var(--color-primary)]"
              />
            </label>

            {/*
             * Removal is behind a confirmation because it is not undoable from this
             * screen. The server refuses it outright while the location still holds
             * birds or still has locations inside it, and says which — so the common
             * mistake answers itself rather than needing a warning here.
             */}
            {confirmingRemove ? (
              <div className="flex flex-col gap-sm rounded-inset bg-error-container p-lg">
                <p className="text-label text-on-error-container">
                  Remove {editing.code}?
                </p>
                <p className="text-caption text-on-error-container">
                  It disappears from every picker. Marking it inactive keeps it out of the
                  way without touching the record.
                </p>

                <div className="flex gap-sm">
                  <Button
                    type="button"
                    variant="danger"
                    disabled={busy}
                    onClick={() => void confirmRemove()}
                  >
                    Remove
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setConfirmingRemove(false)}
                  >
                    Keep it
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="self-start text-error"
                disabled={busy}
                onClick={() => setConfirmingRemove(true)}
              >
                <Trash2 className="size-4" aria-hidden />
                Remove this location
              </Button>
            )}
          </>
        ) : null}
      </form>
    </BottomSheet>
  );
}

/**
 * Everything below `id`, so the "Inside" picker cannot offer a location its own subtree.
 *
 * Walks down from the flat list rather than up from each candidate: one pass over the
 * breeder's own aviary, which is tens of rows.
 */
function descendantsOf(id: string, nodes: readonly LocationNode[]): Set<string> {
  const childrenOf = new Map<string, string[]>();

  for (const node of nodes) {
    if (node.parentId) {
      const siblings = childrenOf.get(node.parentId) ?? [];
      siblings.push(node.id);
      childrenOf.set(node.parentId, siblings);
    }
  }

  const found = new Set<string>();
  const queue = [...(childrenOf.get(id) ?? [])];

  while (queue.length > 0) {
    const next = queue.pop()!;

    // Guards against a cycle already in the data. Without it this is an infinite loop in
    // the one screen a breeder would go to in order to fix that cycle.
    if (found.has(next)) {
      continue;
    }

    found.add(next);
    queue.push(...(childrenOf.get(next) ?? []));
  }

  return found;
}
